// TODO(EX-39079-backend): remove once T4 ships `verification` per attachment
// + `receiptVerificationStatus` rollup on the transaction. This mock fills
// those gaps in a deterministic, tx-id-keyed way so table and flyout agree.

import {
  AttachmentVerification,
  ReceiptMatchingStatuses,
  ReceiptVerificationStatus,
  resolveAttachmentVerification,
} from 'src/models/receiptVerification.models'

// Flip to false to use real backend data only (no client-side mocking).
const MOCK_ENABLED = false

function hash(s: string): number {
  let h = 0
  for (let i = 0; i < s.length; i++) {
    h = (h * 31 + s.charCodeAt(i)) | 0
  }
  return Math.abs(h)
}

function statusForHash(h: number): ReceiptVerificationStatus {
  // 50% verified, 40% not verified, 10% unreadable
  const bucket = h % 10
  if (bucket < 5) return ReceiptVerificationStatus.Verified
  if (bucket < 9) return ReceiptVerificationStatus.NotVerified
  return ReceiptVerificationStatus.Unreadable
}

function reasonsForHash(
  h: number,
  status: ReceiptVerificationStatus,
): string[] {
  if (status === ReceiptVerificationStatus.Verified) {
    return ['Verified amount', 'Verified date', 'Verified merchant']
  }
  if (status === ReceiptVerificationStatus.Unreadable) {
    return []
  }
  const reasons: string[] = []
  if ((h & 1) === 0) reasons.push('Amount cannot be verified')
  if ((h & 2) === 0) reasons.push('Date cannot be verified')
  if ((h & 4) === 0) reasons.push('Merchant cannot be verified')
  // Guarantee at least one reason when not verified
  return reasons.length > 0 ? reasons : ['Amount cannot be verified']
}

export function mockAttachmentVerification(
  transactionId: string,
  index: number,
): AttachmentVerification {
  const h = hash(`${transactionId}:${index}`)
  const status = statusForHash(h)
  return {
    status,
    reasons: reasonsForHash(h, status),
    source: 'MANUAL_UPLOAD',
  }
}

// Aggregate per-attachment reasons for the transaction tooltip. Filters to
// attachments whose glyph type matches the rollup status (positive/negative)
// so the table tooltip reads cleanly — mixing "Verified amount" and "Amount
// cannot be verified" in the same list would pair one of them with the wrong
// glyph. The flyout's per-row tooltips are naturally consistent per row; this
// keeps the aggregate tooltip consistent too.
//
// Source of truth: real `attachment.verification.reasons` when backend
// populates it; otherwise the deterministic mock keyed on (transactionId,
// index) — matching exactly what `injectMockTransactionVerification` fills
// into the flyout attachments.
function isPositiveStatus(s?: ReceiptVerificationStatus): boolean {
  return (
    s === ReceiptVerificationStatus.Verified ||
    s === ReceiptVerificationStatus.AutoMatched
  )
}

function isNegativeStatus(s?: ReceiptVerificationStatus): boolean {
  return (
    s === ReceiptVerificationStatus.NotVerified ||
    s === ReceiptVerificationStatus.Mismatched ||
    s === ReceiptVerificationStatus.Unreadable
  )
}

export function aggregateVerificationReasons(
  transactionId: string | undefined,
  attachmentsCount: number | undefined,
  attachments?: Array<{
    verification?: AttachmentVerification
    matchingStatuses?: ReceiptMatchingStatuses
  }>,
  rollupStatus?: ReceiptVerificationStatus,
): string[] {
  const wantPositive = isPositiveStatus(rollupStatus)
  const wantNegative = isNegativeStatus(rollupStatus)
  const matches = (s?: ReceiptVerificationStatus): boolean => {
    if (!rollupStatus) return true
    if (wantPositive) return isPositiveStatus(s)
    if (wantNegative) return isNegativeStatus(s)
    return false
  }
  const seen = new Set<string>()
  const out: string[] = []
  const push = (v?: AttachmentVerification): void => {
    if (!v || !matches(v.status)) return
    v.reasons?.forEach((r) => {
      if (!seen.has(r)) {
        seen.add(r)
        out.push(r)
      }
    })
  }
  if (attachments && attachments.length > 0) {
    attachments.forEach((a, i) => {
      push(
        resolveAttachmentVerification(a) ??
          (MOCK_ENABLED && transactionId
            ? mockAttachmentVerification(transactionId, i)
            : undefined),
      )
    })
    return out
  }
  if (!MOCK_ENABLED || !transactionId || !attachmentsCount) return out
  for (let i = 0; i < attachmentsCount; i++) {
    push(mockAttachmentVerification(transactionId, i))
  }
  return out
}

export function mockTransactionVerificationStatus(
  transactionId: string | undefined,
  attachmentsCount: number | undefined,
): ReceiptVerificationStatus | undefined {
  if (!MOCK_ENABLED) return undefined
  if (!transactionId || !attachmentsCount) {
    return ReceiptVerificationStatus.NotVerified
  }
  for (let i = 0; i < attachmentsCount; i++) {
    if (
      mockAttachmentVerification(transactionId, i).status ===
      ReceiptVerificationStatus.Verified
    ) {
      return ReceiptVerificationStatus.Verified
    }
  }
  return ReceiptVerificationStatus.NotVerified
}

interface MockTransactionInput<
  A extends { verification?: AttachmentVerification } = {
    verification?: AttachmentVerification
  },
> {
  id?: string
  attachmentsCount?: number
  attachments?: A[]
  receiptMatchingStatus?: ReceiptVerificationStatus
}

export function injectMockTransactionVerification<
  A extends { verification?: AttachmentVerification },
  T extends MockTransactionInput<A>,
>(tx: T): T & { receiptMatchingStatus?: ReceiptVerificationStatus } {
  if (!MOCK_ENABLED) return tx
  const attachments = tx.attachments?.map((a, i) =>
    a.verification
      ? a
      : ({
          ...a,
          verification: mockAttachmentVerification(tx.id ?? 'unknown', i),
        } as A),
  )
  return {
    ...tx,
    receiptMatchingStatus:
      tx.receiptMatchingStatus ??
      mockTransactionVerificationStatus(tx.id, tx.attachmentsCount),
    attachments,
  }
}
